import { type WithRef } from "@common/declarations";
import { isFunction } from "lodash";
import * as React from "react";

export interface WithClickOutsideProps {
  ignoreSelector?: string | string[];
  toggleRef?: React.RefObject<any>;
}

interface WithClickOutsidePropCondition {
  prop: string;
  value: any;
}
type WithClickOutsideFunctionCondition<T extends {}> = (props: T) => boolean;
type WithClickOutsideCondition<T extends {}> = WithClickOutsideFunctionCondition<T> | WithClickOutsidePropCondition;

interface ForwardedRefProps {
  forwardedRef?: React.RefObject<any>;
}

export type WithClickOutsideHOCButtonBeahviour = "left" | "leftRight";
const withClickOutsideHOC = <OriginalProps extends {}>(
  Cmp: React.ComponentType<any>,
  getFnProp: (props: OriginalProps) => () => void,
  condition?: WithClickOutsideCondition<OriginalProps>,
  buttonBehaviour?: WithClickOutsideHOCButtonBeahviour
) => {
  type PublicProps = OriginalProps & WithClickOutsideProps & Partial<WithRef<any>>;
  type Props = React.PropsWithChildren<ForwardedRefProps & PublicProps>;

  class WithClickOutside extends React.PureComponent<Props> {
    public componentDidMount() {
      this._attach(this.props);
    }

    public componentWillUnmount() {
      this._detach();
    }

    public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<{}>, snapshot?: any): void {
      this._detach();
      this._attach(this.props);
    }

    public render() {
      const { forwardedRef, ...props } = this.props;

      return <Cmp ref={forwardedRef} {...props} />;
    }

    private _attach = (props: Props) => {
      if (condition) {
        if (isFunction(condition)) {
          const cond = condition as WithClickOutsideFunctionCondition<OriginalProps>;
          if (!cond(props)) return;
        } else {
          const cond = condition as WithClickOutsidePropCondition;
          if (props[cond.prop] !== cond.value) return;
        }

        // Capture phase is important! Otherwise click hanlder can be executed during mounting
        window.addEventListener("mousedown", this._onMouseDown, true);
      } else {
        window.addEventListener("mousedown", this._onMouseDown, true);
      }
    };

    private _detach = () => {
      window.removeEventListener("mousedown", this._onMouseDown, true);
    };

    private _onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0 && (!buttonBehaviour || buttonBehaviour === "left")) return; // not left button

      const { forwardedRef } = this.props;
      if (!forwardedRef || !forwardedRef.current) {
        console.error("WithClickOutside. forwardedRef or forwardedRef.current is null");
        return;
      }
      if (!e.target) return;

      const node = forwardedRef.current;
      if (!node) {
        console.error("WithClickOutside. forwardedRef.current does not point to a DOM node");
        return;
      }

      const el = node as Element;
      const targetEl = e.target as HTMLElement;
      if (el === targetEl || el.contains(targetEl) || targetEl.closest(".no-close-click-outside")) return;

      const { toggleRef } = this.props;
      if (toggleRef) {
        const domNode = toggleRef.current;
        if (domNode && (domNode === targetEl || domNode.contains(targetEl))) return true;
      }

      getFnProp(this.props)();
    };
  }

  return ({ ref, ...props }: PublicProps & ForwardedRefProps) => {
    return <WithClickOutside {...(props as any)} forwardedRef={props.forwardedRef || ref || React.createRef()} />;
  };
};

export default withClickOutsideHOC;
